import { useEffect, useState } from "react";
import { API_BASE } from "../lib/api";

const ADMIN_KEY = import.meta.env.VITE_ADMIN_KEY;

interface Activity {
  type: "transcription" | "signup" | "chat";
  id: string;
  timestamp: string;
  // transcription fields
  video_id?: string;
  title?: string;
  channel?: string;
  duration_ms?: number;
  user_email?: string;
  // signup fields
  email?: string;
  name?: string;
  // chat fields
  message_count?: number;
}

function formatDuration(ms: number): string {
  if (!ms || !Number.isFinite(ms)) return "-";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export default function AdminActivityView() {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "transcription" | "signup" | "chat">("all");

  useEffect(() => {
    fetch(`${API_BASE}/admin/activity?limit=100`, {
      headers: { "X-Admin-Key": ADMIN_KEY },
    })
      .then((res) => res.json())
      .then((data) => setActivities(data.activities || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? activities : activities.filter((a) => a.type === filter);

  const formatTimeAgo = (timestamp: string) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
        <div className="flex gap-2">
          {(["all", "transcription", "signup", "chat"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                filter === f
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}s
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {filtered.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No activity yet</p>
        ) : (
          filtered.map((a, i) => (
            <div
              key={`${a.type}-${a.id}-${i}`}
              className="flex items-center gap-3 rounded-lg border border-gray-100 bg-white p-3 shadow-sm"
            >
              {/* Icon */}
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                  a.type === "transcription"
                    ? "bg-blue-100 text-blue-600"
                    : a.type === "signup"
                    ? "bg-green-100 text-green-600"
                    : "bg-purple-100 text-purple-600"
                }`}
              >
                {a.type === "transcription" ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                ) : a.type === "signup" ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                )}
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                {a.type === "transcription" && (
                  <>
                    <p className="truncate text-sm font-medium text-gray-900">{a.title}</p>
                    <p className="text-xs text-gray-500">
                      {a.channel} • {a.duration_ms ? formatDuration(a.duration_ms) : "—"} • {a.user_email}
                    </p>
                  </>
                )}
                {a.type === "signup" && (
                  <>
                    <p className="text-sm font-medium text-gray-900">New signup: {a.email}</p>
                    <p className="text-xs text-gray-500">{a.name || "No name"}</p>
                  </>
                )}
                {a.type === "chat" && (
                  <>
                    <p className="truncate text-sm font-medium text-gray-900">{a.title}</p>
                    <p className="text-xs text-gray-500">
                      {a.message_count} messages • {a.user_email}
                    </p>
                  </>
                )}
              </div>

              {/* Time */}
              <span className="shrink-0 text-xs text-gray-400">{formatTimeAgo(a.timestamp)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
